import dayjs from 'dayjs';

import {
  ActivityKindEnum,
  AdFieldOccupancyEnum,
  CompletionStatusEnum,
  PropertyKindEnum,
  TransactionTypeEnum,
} from '../../types/enums';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import { isPropertyAdEditorSelectablePropertyKind } from './propertyAdPropertyKinds';

export type PropertySpecificationField =
  | 'builtUpArea'
  | 'beds'
  | 'baths'
  | 'floor'
  | 'furniture'
  | 'renovation'
  | 'occupancy'
  | 'rentedTill'
  | 'view';

export type PropertySpecificationCompatibility = Record<PropertySpecificationField, boolean>;

const bedsAndBathsKinds = [
  PropertyKindEnum.Apartment,
  PropertyKindEnum.Villa,
  PropertyKindEnum.CommercialVilla,
  PropertyKindEnum.Townhouse,
  PropertyKindEnum.Penthouse,
  PropertyKindEnum.HotelApartment,
];

const floorKinds = [
  PropertyKindEnum.Apartment,
  PropertyKindEnum.HotelApartment,
  PropertyKindEnum.Floor,
  PropertyKindEnum.Office,
  PropertyKindEnum.Warehouse,
];

const landKinds = [
  PropertyKindEnum.ResidentialLand,
  PropertyKindEnum.IndustrialLand,
  PropertyKindEnum.CommercialLand,
];

const furnitureExcludedKinds = [PropertyKindEnum.Warehouse, ...landKinds];
const renovationExcludedKinds = [
  ...furnitureExcludedKinds,
  PropertyKindEnum.HotelApartment,
  PropertyKindEnum.Shop,
];

export const getPropertySpecificationCompatibility = (
  propertyKind: PropertyKindEnum | null
): PropertySpecificationCompatibility => {
  if (propertyKind === null || !isPropertyAdEditorSelectablePropertyKind(propertyKind)) {
    return {
      builtUpArea: false,
      beds: false,
      baths: false,
      floor: false,
      furniture: false,
      renovation: false,
      occupancy: false,
      rentedTill: false,
      view: false,
    };
  }

  const occupancy = !landKinds.includes(propertyKind);

  return {
    builtUpArea: true,
    beds: bedsAndBathsKinds.includes(propertyKind),
    baths: bedsAndBathsKinds.includes(propertyKind),
    floor: floorKinds.includes(propertyKind),
    furniture: !furnitureExcludedKinds.includes(propertyKind),
    renovation: !renovationExcludedKinds.includes(propertyKind),
    occupancy,
    rentedTill: occupancy,
    view: propertyKind !== PropertyKindEnum.Warehouse,
  };
};

export const formatRentedTillForInput = (value: string | null): string =>
  value ? dayjs(value).format('YYYY-MM-DD') : '';

export const parseRentedTillInput = (value: string): string | null =>
  value ? dayjs(value).startOf('day').toDate().toISOString() : null;

export const getVisiblePropertySpecificationFields = ({
  activityKind,
  completionStatus,
  occupancy,
  propertyKind,
  transactionType,
}: Pick<
  PropertyAdEditorFormValues,
  'activityKind' | 'completionStatus' | 'occupancy' | 'propertyKind' | 'transactionType'
>): PropertySpecificationCompatibility => {
  const compatibility = getPropertySpecificationCompatibility(propertyKind);
  const occupancyVisible =
    compatibility.occupancy &&
    activityKind !== ActivityKindEnum.Rent &&
    (transactionType === TransactionTypeEnum.Ready ||
      completionStatus === CompletionStatusEnum.Ready);

  return {
    ...compatibility,
    occupancy: occupancyVisible,
    rentedTill: occupancyVisible && occupancy === AdFieldOccupancyEnum.Tenanted,
    renovation:
      compatibility.renovation && (occupancyVisible || activityKind === ActivityKindEnum.Rent),
  };
};
